import { brandApi } from '@/api/account-management/brandApi';
import { locationApi } from '@/api/account-management/locationApi';
import { organizationApi } from '@/api/account-management/organizationApi';
import { type Brand } from '@/models/account-management/Brand';
import { type Location } from '@/models/account-management/Location';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from "../../theme";
import Spacer from '../common/Spacer';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import { useToast } from '../common/toast/ToastProvider';
import NewLocationDialog from './NewLocationDialog';

const LocationList = () => {
    const [newPopoverAnchorEl, setNewPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [newDialogOpen, setNewDialogOpen] = useState(false);
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const navigate = useNavigate();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.adminConfiguration);
    const canUpdate = perms.canUpdate(Perm.adminConfiguration);
    const canDelete = perms.canDelete(Perm.adminConfiguration);

    // Scope comes from the global top-bar selector.
    const organizationId = app.organizationId ?? undefined;
    const brandId = app.brandId ?? undefined;

    // Only needed to resolve the selected brand's type for the "New location" flow.
    const { data: brandList } = useQuery<Brand[]>({ queryKey: ['brands', organizationId], enabled: !!organizationId, queryFn: () => organizationApi.listBrands(organizationId!), staleTime: Infinity });
    const { data: locationList, isFetching: isGridLoading } = useQuery<Location[]>({ queryKey: ['locations', brandId ?? ''], enabled: !!brandId, queryFn: () => brandApi.listLocations(brandId!), staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: false }); // refetch on every visit; isFetching drives the grid loading overlay so refreshes are visible
    const { mutateAsync: deleteLocation } = useMutation({ mutationFn: locationApi.delete, onSuccess: () => handleDeleteSuccess(), onError: () => handleDeleteError() });

    const selectedBrand = useMemo(() => brandList?.find(m => m.id === brandId), [brandList, brandId]);

    const columns: GridColDef<Location>[] = [
        {
            field: 'name',
            headerName: strings.name,
            flex: 2,
        },
        {
            field: 'enabled',
            headerName: strings.enabled,
            type: 'boolean',
            flex: 1
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditClick} aria-label="edit location" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={getDeleteWarningMessage} onClick={handleDeleteClick} aria-label="delete location" hidden={!canDelete} />
            ],
        },
    ];

    const handleAddClick: React.MouseEventHandler<HTMLButtonElement> = () => {
        if (!brandId || !selectedBrand) return;
        setNewDialogOpen(true);
    };

    const handleAddRestaurantClick = () => {
        if (!brandId) return;
        navigate(`/touchtab/admin/configuration/locations/new?type=0&orgId=${organizationId}&brandId=${brandId}`);
    };

    const handleAddRetailClick = () => {
        if (!brandId) return;
        navigate(`/touchtab/admin/configuration/locations/new?type=1&orgId=${organizationId}&brandId=${brandId}`);
    };

    const handleAddServiceClick = () => {
        if (!brandId) return;
        navigate(`/touchtab/admin/configuration/locations/new?type=2&orgId=${organizationId}&brandId=${brandId}`);
    };

    // Edit (row) button handler
    const handleEditClick = (id: string) => {
        const found = locationList?.find(o => o.id === id);
        if (!found) return;
        navigate(`/touchtab/admin/configuration/locations/${found.slug}`);
    };

    const getDeleteWarningMessage = (id: string) => {
        const loc = locationList?.find(o => o.id === id);
        return strings.locationConfirmDeleteMessage.replace("{{name}}", loc?.name ?? "");
    }

    const handleDeleteClick = async (id: string) => {
        await deleteLocation(id);
    };

    const handleDeleteSuccess = async () => {
        toast({ severity: "success", message: strings.locationDeleteSuccess });
        await qc.invalidateQueries({ queryKey: ['locations', brandId] });
        // A deleted location must also drop out of the onboarding list.
        await qc.invalidateQueries({ queryKey: ['onboarding-pending'] });
    }

    const handleDeleteError = () => {
        toast({ severity: "error", message: strings.locationDeleteError });
    }

    return (
        <>
            <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
                {canCreate &&
                    <Stack direction="row" alignItems="center" justifyContent="flex-end">
                        <Button
                            id="new-location-button"
                            variant="contained"
                            startIcon={<AddIcon />}
                            disabled={!brandId}
                            onClick={handleAddClick}
                            size="input"
                        >
                            {strings.locationNewTitle}
                        </Button>
                    </Stack>
                }
                <Spacer y={2} />
                <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                    <DataGrid
                        rows={locationList}
                        columns={columns}
                        autoPageSize={true}
                        loading={!brandId ? false : isGridLoading}
                        localeText={{ noRowsLabel: !brandId ? strings.selectBrand : strings.locationsNotFound }}
                    />
                </Box>
            </Box>

            <Menu
                anchorEl={newPopoverAnchorEl}
                open={Boolean(newPopoverAnchorEl)}
                onClose={() => setNewPopoverAnchorEl(null)}
                aria-labelledby="new-location-button"
            >
                <MenuItem onClick={handleAddRestaurantClick}>
                    <ListItemIcon>
                        <RestaurantIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{strings.locationNewRestaurant}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleAddRetailClick}>
                    <ListItemIcon>
                        <ShoppingCartIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{strings.locationNewRetail}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleAddServiceClick}>
                    <ListItemIcon>
                        <LocalShippingIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{strings.locationNewService}</ListItemText>
                </MenuItem>
            </Menu>

            {selectedBrand && organizationId && brandId &&
                <NewLocationDialog
                    open={newDialogOpen}
                    onClose={() => setNewDialogOpen(false)}
                    type={selectedBrand.type}
                    organizationId={organizationId}
                    brandId={brandId}
                />
            }
        </>
    );
};

export default LocationList;
